import serviceBanner from '@/../../public/storage/images/service-banner.jpg';
import shoppeBanner from '@/../../public/storage/images/shoppe-banner.jpg';
import CourseCard from '@/components/CourseCard';
import InquiryForm from '@/components/InquiryForm';
import Marquee from '@/components/Marquee';
import { Reveal, RevealGroup, RevealItem } from '@/components/Reveal';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselControls,
  CarouselItem,
} from '@/components/ui/carousel';
import Typography from '@/components/ui/typography';
import { WhatsAppIcon } from '@/components/WhatsAppButton';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { siteStats, spellOutNumber } from '@/lib/siteStats';
import { cn, whatsAppHref } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import {
  BadgeCheck,
  GraduationCap,
  MoveRight,
  Phone,
  Scissors,
  Users,
} from 'lucide-react';

// A plain /storage path, not a Vite import: this is the hero's LCP image, and
// app.blade.php preloads it by this exact URL (home route only) so the
// browser's preload scanner can discover it straight from the initial HTML
// instead of waiting on the JS bundle to load and render the <img>. A Vite
// import would hash the filename at build time, and that hash isn't known to
// the Blade template.
const courseBanner = '/storage/images/course-banner.webp';

interface FeaturedCourses {
  title: string;
  slug: string;
  description: string;
  image_url: string;
  rating_count: number;
  review_count: number;
  course_category_id: number;
}

interface CourseCategorySummary {
  id: number;
  title: string;
  slug: string;
  summary: string;
  image_url: string;
  courses_count: number;
}

/*
 * Icons rather than the stock illustrations that were here — those were generic
 * marketplace art and were the most templated thing on the page. Scissors and
 * the graduation cap at least belong to the subject.
 *
 * The wording is tightened, not re-claimed: no promise here goes beyond what the
 * site already states elsewhere.
 */
const features = [
  {
    title: 'Taught by people who do the work',
    icon: GraduationCap,
    description:
      'Every course is built and taught by someone who has worked in the field, not only studied it.',
  },
  {
    title: 'Hands-on, not theory',
    icon: Scissors,
    description:
      'You work on real projects and practical exercises from the start, rather than sitting through lectures first.',
  },
  {
    title: 'Certificates that travel',
    icon: BadgeCheck,
    description:
      'Courses are designed around industry-recognised certification, so the qualification means something outside our walls.',
  },
  {
    title: 'A batch that stays with you',
    icon: Users,
    description:
      'Learn alongside a group you keep in touch with — for advice, for work, and for the occasional referral.',
  },
];

/* Claims the academy already makes on this site, gathered in one place. */
const credentials = [
  {
    image: '/storage/ISO9001.webp',
    alt: 'ISO 9001:2015 certification mark',
    title: 'ISO 9001:2015 certified',
    detail: 'Audited quality management, not a self-issued badge.',
  },
  {
    image: '/storage/NCVTC-logo.webp',
    alt: 'NCVTC Educational Training and Placement Council emblem',
    title: 'NCVTC affiliated',
    detail:
      'Educational Training and Placement Council, State and Central Government Act, Govt. of India.',
  },
];

const { years, branches, courses, services } = siteStats;

const stats = [
  { title: `${years}+`, description: 'years teaching' },
  { title: `${branches}`, description: 'branches' },
  { title: `${courses}+`, description: 'courses' },
  { title: `${services}+`, description: 'services' },
];

/*
 * The three things the academy does, in the order someone moves through them:
 * learn the craft, buy what it needs, or hire someone who already has it.
 * Laid out as a bento — the first card carries the weight, the other two sit
 * beside it — rather than three equal columns.
 */
const pillars = [
  {
    image: courseBanner,
    label: 'Learn it',
    title: 'Courses',
    description:
      'Fashion design, tailoring, aari, beauty, handwriting and more — taught in person, from the first stitch.',
    link: 'courses',
    cta: 'Browse courses',
  },
  {
    image: shoppeBanner,
    label: 'Make it',
    title: 'Online Shoppe',
    description:
      'Fabric, aari frames, threads and tools, shipped from our own stock.',
    link: 'shoppe',
    cta: 'Visit the shoppe',
  },
  {
    image: serviceBanner,
    label: 'Order it',
    title: 'Services',
    description:
      'Book a tailor, a beautician, an aari specialist or a tutor — every one vetted by us.',
    link: 'services',
    cta: 'See services',
  },
];

/*
 * Written out as literals, not built as `cat-${i}`: these live in @layer
 * components and Tailwind removes any class it cannot find verbatim in the
 * scanned source, so an interpolated name would be purged from the bundle.
 */
const categoryGradients = ['cat-0', 'cat-1', 'cat-2', 'cat-3', 'cat-4'];

const Home = ({
  featured_courses,
  course_categories,
  founder_image,
  pillar_image,
  contact_phone,
}: {
  featured_courses: FeaturedCourses[];
  course_categories: CourseCategorySummary[];
  founder_image: string | null;
  pillar_image: string | null;
  contact_phone: string | null;
}) => {
  // Server-rendered rather than read from CategoryContext, which only arrives
  // after a POST to /api/categories on mount — so this content is now in the
  // initial HTML instead of appearing a beat later.
  const categoryFor = (id: number) =>
    course_categories.find((category) => category.id === id);

  const ribbon =
    course_categories.length > 0
      ? course_categories.map((category) => category.title)
      : ['Fashion design', 'Tailoring', 'Aari work', 'Beautician', 'Zumba'];

  return (
    <>
      {/* Hero — ink, woven, oversized */}
      {/* The pattern is layered here rather than via Band's `pattern` prop so it
          sits over the photograph instead of behind it. */}
      <Band tone="ink" className="overflow-hidden">
        <div aria-hidden className="absolute inset-0">
          <img
            className="size-full object-cover opacity-30"
            src={courseBanner}
            alt=""
            width={1600}
            height={1067}
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-linear-to-t from-ink via-ink/85 to-ink/55 md:bg-linear-to-r md:via-ink/80 md:to-ink/30" />
          <div className="pattern-weave pattern-on-ink absolute inset-0 opacity-70" />
        </div>

        <SectionLayout
          space="none"
          className="relative py-20 md:py-28 lg:py-36"
        >
          <RevealGroup
            trigger="load"
            stagger={0.08}
            className="flex max-w-3xl flex-col items-start gap-6"
          >
            <RevealItem className="flex items-center gap-4">
              <Typography.Eyebrow>
                Trichy &middot; since 2011
              </Typography.Eyebrow>
              <div aria-hidden className="seam seam-on-ink w-16" />
            </RevealItem>

            <RevealItem>
              {/* Deliberately larger than the Display1 default — the hero is the
                  one place the type should feel oversized. */}
              <Typography.Display1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
                Learn a craft you can earn from.
              </Typography.Display1>
            </RevealItem>

            <RevealItem>
              <Typography.Body className="max-w-xl text-ink-foreground/75">
                Fashion design, tailoring, aari embroidery, beauty and more —
                taught hands-on across {spellOutNumber(branches)} branches in
                Trichy, Chennai and Kumbakonam, and certified to ISO 9001:2015
                at the end of it.
              </Typography.Body>
            </RevealItem>

            <RevealItem className="flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link href={route('courses')}>
                  Browse courses
                  <MoveRight size={18} className="ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-ink-foreground/30 bg-transparent text-ink-foreground hover:bg-ink-foreground/10 hover:text-ink-foreground"
              >
                <Link href={route('contact')}>Talk to us</Link>
              </Button>
            </RevealItem>
          </RevealGroup>

          {/* Track record, pinned to the hero rather than given its own band. */}
          <RevealGroup
            trigger="load"
            delay={0.3}
            className="mt-14 grid max-w-3xl grid-cols-2 gap-y-8 border-t border-ink-foreground/15 pt-8 md:grid-cols-4"
          >
            {stats.map((stat) => (
              <RevealItem
                key={stat.description}
                className="flex flex-col gap-1"
              >
                {/* Not a heading — these are figures, and Display3 was
                    rendering each one as an <h3> for screen readers. */}
                <span className="font-display text-3xl font-semibold tracking-tight text-primary md:text-4xl">
                  {stat.title}
                </span>
                <Typography.Small className="text-ink-foreground/60">
                  {stat.description}
                </Typography.Small>
              </RevealItem>
            ))}
          </RevealGroup>
        </SectionLayout>
      </Band>

      {/* Ribbon of what's taught */}
      <div className="w-full bg-primary text-primary-foreground">
        <Marquee items={ribbon} durationSeconds={45} />
      </div>

      {/* Course categories */}
      <Band tone="deep" pattern="stitch">
        <SectionLayout space="lg">
          <Reveal className="flex flex-col gap-3">
            <Typography.Eyebrow>What you can learn</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              Tailoring, beauty, embroidery and more
            </Typography.Display2>
            <Typography.Body className="max-w-prose text-muted-foreground">
              Every category runs at all {spellOutNumber(branches)} branches,
              and every course ends in an ISO 9001:2015 certificate.
            </Typography.Body>
          </Reveal>

          {/* Bento: the first category runs double-width and double-height,
              the rest fill around it. Works for any number of categories —
              only index 0 is special-cased. */}
          <RevealGroup className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-60">
            {course_categories.map((category, index) => {
              const lead = index === 0;

              return (
                <RevealItem
                  key={category.slug}
                  className={cn('flex', lead && 'lg:col-span-2 lg:row-span-2')}
                >
                  <article className="group relative aspect-4/3 w-full overflow-hidden rounded-xl shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-pop lg:aspect-auto">
                    <img
                      className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-105"
                      src={category.image_url}
                      alt={`${category.title} classes at Samuthrika Academy`}
                      loading="lazy"
                    />

                    {/* The colour wash that gives each category its identity. */}
                    <div
                      aria-hidden
                      className={cn(
                        'cat-gradient absolute inset-0 opacity-75 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-55',
                        categoryGradients[index % categoryGradients.length]
                      )}
                    />
                    {/* Legibility scrim — the text sits on ink, never on the
                        colour, which would fail contrast at the amber end. */}
                    <div
                      aria-hidden
                      className="absolute inset-0 bg-linear-to-t from-ink via-ink/45 to-transparent"
                    />

                    <div className="relative flex h-full flex-col items-start justify-end gap-2 p-5 text-ink-foreground md:p-6">
                      {/* "available" rather than "{n} courses" — category
                          names already end in "Courses", so the count would
                          repeat the word right above the heading. */}
                      <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                        {category.courses_count} available
                      </span>

                      <Typography.Display3
                        className={cn(
                          'text-white',
                          lead
                            ? 'text-2xl md:text-3xl lg:text-4xl'
                            : 'text-xl md:text-xl lg:text-2xl'
                        )}
                      >
                        {category.title}
                      </Typography.Display3>

                      {lead && (
                        <Typography.Body className="line-clamp-2 max-w-md text-white/80">
                          {category.summary}
                        </Typography.Body>
                      )}

                      {/* Stretched link — the whole card is the target, but the
                          anchor text names the category rather than saying
                          "view courses" five times over. */}
                      <Link
                        href={route('courses.showCategory', category.slug)}
                        className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-white after:absolute after:inset-0 after:content-['']"
                      >
                        View all {category.title}
                        <MoveRight
                          size={16}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </Link>
                    </div>
                  </article>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </SectionLayout>
      </Band>

      {/* Featured courses — the band is dropped entirely when nothing is
          featured, rather than rendering a heading over an empty carousel with
          live prev/next controls. */}
      {featured_courses.length > 0 && (
        <Band tone="cream">
          <SectionLayout space="lg">
            <Reveal className="flex flex-col gap-3">
              <Typography.Eyebrow>Starting soon</Typography.Eyebrow>
              <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
                Popular courses right now
              </Typography.Display2>
            </Reveal>

            <Carousel className="flex w-full flex-col gap-6">
              <CarouselContent className="-ml-4">
                {featured_courses.map((course) => {
                  const category = categoryFor(course.course_category_id);

                  return (
                    <CarouselItem
                      key={course.slug}
                      /* Three across at the widest, not four: at max-w-7xl a
                       quarter-width card came to ~296px, which left the 16:9
                       image too small to read. Three gives ~395px. */
                      className="basis-11/12 pl-4 sm:basis-1/2 lg:basis-1/3"
                    >
                      <CourseCard
                        category={{ slug: category?.slug ?? '' }}
                        course={course}
                        categoryLabel={category?.title}
                      />
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselControls />
            </Carousel>
          </SectionLayout>
        </Band>
      )}

      {/* The three pillars — one photograph, three routes in */}
      <Band tone="surface" pattern="lattice">
        <SectionLayout space="lg">
          <Reveal className="flex flex-col gap-3">
            <Typography.Eyebrow>Three ways in</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              Learn the craft, or let us do it for you
            </Typography.Display2>
          </Reveal>

          {/*
            One image, three rows.

            The previous split layout put the lead image above the Shoppe card in
            the left column and text above the Services card on the right, so the
            left read as two stacked photographs and the right as one — an
            imbalance no amount of spacing fixes. Carrying a single image and
            listing the three routes beside it removes the comparison entirely,
            and the rows cannot strand a CTA the way the tall card did.
          */}
          <RevealGroup className="grid gap-6 md:grid-cols-5">
            <RevealItem className="md:col-span-2">
              <div className="relative h-full min-h-64 overflow-hidden rounded-xl md:min-h-104">
                {/* Decorative either way: the overlaid heading carries the
                    meaning, and the fallback (course-banner.webp) is a stock
                    illustration rather than a photograph of the academy, so a
                    descriptive alt would be false whenever it is the one shown.

                    object-position is pulled left of centre: the frame is
                    taller than the 4:3 source, so a centred crop keeps the
                    awards shelf but slices the child at the right edge. 32%
                    holds the branded wall and the certificate and drops that
                    edge cleanly. */}
                <img
                  aria-hidden
                  className="absolute inset-0 size-full object-cover object-[32%_center]"
                  src={pillar_image ? `/storage/${pillar_image}` : courseBanner}
                  alt=""
                  loading="lazy"
                />
                {/* Lighter than the categories wash: this is a real photograph
                    with its own colour, and a 55% orange multiply turned the
                    teal in it to mud. */}
                <div
                  aria-hidden
                  className="cat-gradient cat-0 absolute inset-0 opacity-40 mix-blend-multiply"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-linear-to-t from-ink via-ink/30 to-transparent"
                />
                <div className="relative flex h-full flex-col justify-end gap-2 p-6">
                  <Typography.Display3 className="text-2xl text-white md:text-2xl lg:text-3xl">
                    <span className="capitalize">
                      {spellOutNumber(branches)}
                    </span>{' '}
                    branches across Tamil Nadu
                  </Typography.Display3>
                  <Typography.Subtle className="text-white/80">
                    Everything alongside runs at every one of them.
                  </Typography.Subtle>
                </div>
              </div>
            </RevealItem>

            <RevealItem className="flex flex-col justify-center md:col-span-3">
              {pillars.map((pillar, index) => (
                <div key={pillar.link}>
                  {index > 0 && <div aria-hidden className="seam" />}
                  <Link
                    href={route(pillar.link)}
                    className="group flex items-start gap-5 py-6 md:py-7"
                  >
                    <div className="flex flex-1 flex-col items-start gap-2">
                      <Typography.Eyebrow>{pillar.label}</Typography.Eyebrow>
                      <Typography.Display3 className="text-2xl transition-colors duration-300 group-hover:text-primary md:text-2xl lg:text-3xl">
                        {pillar.title}
                      </Typography.Display3>
                      <Typography.Subtle className="max-w-prose">
                        {pillar.description}
                      </Typography.Subtle>
                    </div>

                    <span
                      aria-hidden
                      className="mt-9 inline-flex size-11 shrink-0 items-center justify-center rounded-full border text-muted-foreground transition-all duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground"
                    >
                      <MoveRight
                        size={18}
                        className="transition-transform duration-300 group-hover:translate-x-0.5"
                      />
                    </span>
                  </Link>
                </div>
              ))}
            </RevealItem>
          </RevealGroup>
        </SectionLayout>
      </Band>

      {/*
        Replaced the mission/vision pair that sat here. Those statements — "the
        transformative power of learning", "a global hub" — could have belonged
        to any school anywhere, and gave someone choosing a tailoring course
        nothing to decide on. The founder is the opposite: specific, verifiable,
        and already the face inside the logo.
      */}
      <Band tone="indigo" pattern="stitch" patternOpacity="medium">
        <SectionLayout space="lg">
          <RevealGroup className="grid items-center gap-10 md:grid-cols-5">
            <RevealItem className="md:col-span-2">
              <div className="relative mx-auto max-w-xs md:mx-0">
                <div
                  aria-hidden
                  className="cat-gradient cat-0 absolute -inset-2 rounded-2xl opacity-70 blur-xs"
                />
                {founder_image ? (
                  <img
                    className="relative aspect-4/5 w-full rounded-xl object-cover object-center shadow-pop"
                    src={`/storage/${founder_image}`}
                    alt="Rtn Thamizhachi Priya Govindaraj, founder of Samuthrika Academy"
                    loading="lazy"
                  />
                ) : null}
              </div>
            </RevealItem>

            <RevealItem className="flex flex-col items-start gap-5 md:col-span-3">
              <Typography.Eyebrow>Founder</Typography.Eyebrow>
              <Typography.H2>Rtn Thamizhachi Priya Govindaraj</Typography.H2>
              <Typography.Body className="max-w-prose text-indigo-foreground/80">
                {years} years ago she started with no investment and a skill
                worth teaching. Today the academy runs{' '}
                {spellOutNumber(branches)} branches across Tamil Nadu, and she
                has been recognised with more than fifty awards for the work.
              </Typography.Body>

              <ul className="flex flex-wrap gap-3">
                {[
                  { value: '50+', label: 'awards' },
                  { value: `${years}`, label: 'years' },
                  { value: 'Rotarian', label: 'and community leader' },
                ].map((item) => (
                  <li
                    key={item.label}
                    className="rounded-full border border-indigo-foreground/25 bg-indigo-foreground/10 px-4 py-1.5 text-sm"
                  >
                    <span className="font-semibold">{item.value}</span>{' '}
                    <span className="text-indigo-foreground/70">
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                asChild
                variant="outline"
                className="mt-2 border-indigo-foreground/30 bg-transparent text-indigo-foreground hover:bg-indigo-foreground/10 hover:text-indigo-foreground"
              >
                <Link href={route('founder')}>
                  Read her story
                  <MoveRight size={16} className="ml-2" />
                </Link>
              </Button>
            </RevealItem>
          </RevealGroup>
        </SectionLayout>
      </Band>

      {/* Why here — proof on the left, reasons on the right */}
      <Band tone="raised" pattern="lattice" seam>
        <SectionLayout space="lg">
          <Reveal className="flex flex-col gap-3">
            <Typography.Eyebrow>Why here</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              What sets us apart
            </Typography.Display2>
            <Typography.Body className="max-w-prose text-muted-foreground">
              {years} years, {spellOutNumber(branches)} branches, and a
              certificate that is recognised beyond our own classrooms.
            </Typography.Body>
          </Reveal>

          <RevealGroup className="grid gap-4 lg:grid-cols-3">
            {/* The credentials card carries the section — it is the one claim
                here backed by a document rather than a promise. */}
            <RevealItem className="flex lg:row-span-2">
              <article className="relative flex w-full flex-col justify-between gap-10 overflow-hidden rounded-xl bg-ink p-7 text-ink-foreground md:p-8">
                <div
                  aria-hidden
                  className="pattern-weave pattern-on-ink absolute inset-0 opacity-70"
                />

                <div className="relative flex flex-col gap-3">
                  <Typography.Eyebrow>Proof</Typography.Eyebrow>
                  <Typography.Display3 className="text-2xl md:text-2xl lg:text-3xl">
                    Credentials you can check
                  </Typography.Display3>
                  <Typography.Subtle className="text-ink-foreground/70">
                    Registered, audited and affiliated — every one of these is a
                    document you are welcome to ask for.
                  </Typography.Subtle>
                </div>

                <ul className="relative flex flex-col gap-5">
                  {credentials.map((credential) => (
                    <li
                      key={credential.title}
                      className="flex items-start gap-4"
                    >
                      <span className="inline-flex size-14 shrink-0 items-center justify-center rounded-lg bg-white p-1.5">
                        <img
                          className="size-full object-contain"
                          src={credential.image}
                          alt={credential.alt}
                          width={128}
                          height={128}
                          loading="lazy"
                        />
                      </span>
                      <span className="flex flex-col gap-1">
                        <span className="font-semibold">
                          {credential.title}
                        </span>
                        <Typography.Small className="text-ink-foreground/60">
                          {credential.detail}
                        </Typography.Small>
                      </span>
                    </li>
                  ))}

                  <li className="flex items-start gap-4">
                    <span
                      aria-hidden
                      className="inline-flex size-14 shrink-0 items-center justify-center rounded-lg border border-ink-foreground/20 text-4xl font-semibold leading-none"
                    >
                      &#174;
                    </span>
                    <span className="flex flex-col gap-1">
                      <span className="font-semibold">
                        Trademark registered
                      </span>
                      <Typography.Small className="text-ink-foreground/60">
                        Samuthrika Academy is a registered mark, in Trichy since
                        2011.
                      </Typography.Small>
                    </span>
                  </li>
                </ul>
              </article>
            </RevealItem>

            {features.map((feature, index) => (
              <RevealItem key={feature.title} className="flex">
                <article className="group flex w-full flex-col items-start gap-4 rounded-xl border bg-card p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-pop md:p-7">
                  {/* Chips reuse the category ramps, so the two sections read as
                      one system rather than two palettes. */}
                  <span
                    className={cn(
                      'cat-gradient inline-flex size-12 items-center justify-center rounded-xl text-white shadow-card transition-transform duration-300 group-hover:scale-105',
                      categoryGradients[index % categoryGradients.length]
                    )}
                  >
                    <feature.icon size={22} strokeWidth={1.75} />
                  </span>
                  <Typography.Large>{feature.title}</Typography.Large>
                  <Typography.Subtle>{feature.description}</Typography.Subtle>
                </article>
              </RevealItem>
            ))}
          </RevealGroup>
        </SectionLayout>
      </Band>

      {/*
        Closing band, reordered to sit after the trust block rather than before
        the course content.

        It carries the page's only phone number and its only form: previously
        every conversion here cost a navigation, which is the wrong shape for a
        local business whose enquiries arrive by phone. The form sits on a card
        so the default (light) form controls keep working inside an ink band.
      */}
      <Band tone="ink" pattern="lattice" patternOpacity="medium">
        <SectionLayout space="lg">
          <RevealGroup className="grid items-start gap-10 lg:grid-cols-2 lg:gap-14">
            <RevealItem className="flex flex-col items-start gap-6">
              <Typography.Eyebrow>Next batch</Typography.Eyebrow>
              <Typography.Display2>
                Come and see a class before you decide.
              </Typography.Display2>
              <Typography.Body className="max-w-xl text-ink-foreground/75">
                Walk into any of our branches in Trichy, Chennai or Kumbakonam,
                or send us a message and we will call you back.
              </Typography.Body>

              {contact_phone ? (
                <div className="flex items-center gap-3">
                  <a
                    href={`tel:+91${contact_phone}`}
                    className="group inline-flex items-center gap-3 rounded-full border border-ink-foreground/25 py-2 pl-2 pr-5 transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <span className="inline-flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors duration-300 group-hover:bg-primary-foreground group-hover:text-primary">
                      <Phone size={16} />
                    </span>
                    <span className="font-display text-lg font-semibold tracking-tight">
                      +91 {contact_phone}
                    </span>
                  </a>

                  <a
                    href={whatsAppHref(contact_phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat on WhatsApp"
                    className="inline-flex size-12 items-center justify-center rounded-full border border-ink-foreground/25 transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <WhatsAppIcon className="size-5" />
                  </a>
                </div>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild size="lg">
                  <Link href={route('courses')}>
                    Browse courses
                    <MoveRight size={18} className="ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-ink-foreground/30 bg-transparent text-ink-foreground hover:bg-ink-foreground/10 hover:text-ink-foreground"
                >
                  <Link href={route('contact')}>Find a branch</Link>
                </Button>
              </div>
            </RevealItem>

            <RevealItem>
              <div
                id="inquiry"
                className="scroll-mt-28 rounded-xl bg-card p-6 text-foreground shadow-pop md:p-8"
              >
                <div className="mb-6 flex flex-col gap-2">
                  <Typography.Eyebrow>Enquiries</Typography.Eyebrow>
                  <Typography.Display3 className="text-2xl md:text-2xl lg:text-3xl">
                    Send us a message
                  </Typography.Display3>
                </div>

                <InquiryForm />
              </div>
            </RevealItem>
          </RevealGroup>
        </SectionLayout>
      </Band>
    </>
  );
};

export default Home;
